import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
import { WorkflowStepBatchConfigSchema } from "../../types/batch";
import { QUANTITY_INPUT } from "./quantityInput";

/**
 * Upsert Inventory Snapshot
 *
 * Takes ONE snapshot's fields as single-value input sockets, shaped after the
 * Gently model (`im_inventory_snapshots`) — not after any source system. A CDC
 * observer or file parser maps its own columns into these sockets, so the
 * same step serves every source; anything source-specific (field naming,
 * sentinel values, code decoding) belongs in that upstream mapping.
 *
 * The warehouse is resolved from its origin code and the product from its SKU
 * (optionally scoped by the `originSystem` config); rows referencing unknown
 * warehouses or products are skipped and reported per input, never
 * auto-created — the product catalog and warehouse seed remain the source of
 * truth. With a `batch` config the engine hands whole chunks to the bulk
 * handler. A leaf step: per-row outputs are execution results, not a
 * downstream data contract.
 */
export const upsertInventorySnapshotStep = defineStepConfig({
  type: "upsert_inventory_snapshot",
  name: "Upsert Inventory Snapshot",
  description:
    "Upserts one inventory snapshot (warehouse code, product SKU, date, on-hand/reserved quantities). Resolves the warehouse and product, then writes the snapshot in source-agnostic batches. Fan a trigger's per-field array outputs into its single-value inputs and add a batch config to process rows in chunks.",
  category: "Transformation",
  libraryModule: "Procurement",

  inputSchema: z.object({
    // Nullable because a real feed carries rows with neither. GAINS ONHNUNSH
    // opens with header-ish records whose GILOC is blank (8 of them in QE's
    // file), and fixed-width parsing yields NULL for an all-blank field. Input
    // is validated BEFORE the handler runs and a parse failure is non-retryable
    // (executeStep), and the batch path shares one activity per chunk — so a
    // non-nullable socket would fail a whole chunk of good rows over a few junk
    // ones. A missing code is simply a reference that resolves to nothing, which
    // this step already reports per row.
    productSku: z
      .string()
      .nullable()
      .describe("Product SKU (resolved to the product); null is unresolvable"),
    warehouseCode: z
      .string()
      .nullable()
      .describe(
        "Warehouse origin code (resolved to the warehouse); null is unresolvable",
      ),
    // Optional because a live stock feed has no as-of column: neither AS/400
    // `as_stock` nor the GAINS ONHNUNSH file carries the date, and the generic
    // file-ingest path (blob adapter → DataSource writer) loads exactly the
    // parsed columns without injecting file metadata. Left unwired, the run date
    // (UTC) is stamped; wire it only for a feed that knows its own as-of date.
    // `z.iso.date()`, not `z.string()`: this socket is operator-wirable to any
    // ingest column, and the value reaches the `snapshot_date` bind directly.
    // A timestamp would be truncated by Postgres but NOT by the conflict key the
    // service builds, so dedupe and ON CONFLICT would both miss and the run
    // would insert a row that should have been an update. Anything malformed
    // would fail the whole chunk's INSERT — the same blast radius this step just
    // removed for null identifiers. Validating at the boundary keeps the check
    // out of the internal service.
    snapshotDateUtc: z.iso
      .date()
      .optional()
      .describe(
        "Snapshot date in UTC, YYYY-MM-DD. Leave unwired to stamp the run date (UTC) — so a snapshot can read one day ahead of a North American operator's calendar, by design.",
      ),
    /**
     * DEPRECATED pre-rename name for `snapshotDateUtc`, still accepted.
     *
     * Socket ids are persisted in `wfv2_workflow_steps.input_sockets` and edges
     * target them by id, and definitions are VERSION-PINNED — so a definition
     * wired before the rename still supplies `snapshotDate`. `inputSchema` is a
     * `z.object`, which STRIPS unknown keys, so dropping this outright would not
     * error: the date would silently vanish and the row would be stamped with
     * the run date instead of the one the workflow supplied. Wrong data beats no
     * data only in the sense that nobody notices.
     *
     * Remove once persisted definitions have been migrated onto the new id.
     */
    snapshotDate: z.iso
      .date()
      .optional()
      .describe(
        "DEPRECATED — use snapshotDateUtc. Accepted so workflows wired before the rename keep their date.",
      ),
    // Accepts a numeric string as well as a number: Postgres returns NUMERIC
    // columns as strings (node-postgres does this to preserve arbitrary
    // precision), so a CDC row carries "102.00" rather than 102. The value is
    // written to a DECIMAL column as a string either way, so passing the string
    // through avoids a float round-trip that could lose precision on wide
    // decimals.
    onHandQuantity: QUANTITY_INPUT.describe("On-hand quantity"),
    reservedQuantity: QUANTITY_INPUT.describe("Reserved quantity"),
  }),

  outputSchema: z.object({
    written: z.boolean().describe("The snapshot row was upserted"),
    skipped: z
      .boolean()
      .describe("The row was skipped because a reference was unresolved"),
    unresolvedProduct: z
      .boolean()
      .describe(
        "No product matched the SKU (ingest the product catalog first)",
      ),
    unresolvedWarehouse: z
      .boolean()
      .describe("No warehouse matched the warehouse code"),
  }),

  configSchema: z.object({
    batch: WorkflowStepBatchConfigSchema.prefault({ size: 5000 }),
    originSystem: z
      .string()
      .optional()
      .describe(
        "Restrict WAREHOUSE resolution to references ingested from this origin system. Does not scope products: ProductV3 is keyed by SKU within the organization and carries no origin system. Leave empty to match any.",
      ),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "30 minutes",
    retry: { maximumAttempts: 2 },
  },
});
